import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'i18n'
import styled from 'styled-components'
import { Body, Link, LinkStyle } from 'components/styled/typography'

const propTypes = {
  i18n: PropTypes.shape({
    supportLink: PropTypes.string,
    footer: PropTypes.shape({
      terms: PropTypes.string,
      accessibilityReport: PropTypes.string,
      privacyPolicy: PropTypes.string,
      license: PropTypes.string,
      customSupport: PropTypes.func,
    }),
  }).isRequired,
  termsLink: PropTypes.string.isRequired,
  licenseLink: PropTypes.string,
  accessibilityReportLink: PropTypes.string.isRequired,
  privacyPolicyLink: PropTypes.string,
  customSupport: PropTypes.shape({
    companyName: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
  isEmbeddedPage: PropTypes.bool,
}

const defaultProps = {
  customSupport: undefined,
  privacyPolicyLink: undefined,
  licenseLink: undefined,
  isEmbeddedPage: false,
}

const CustomSupportBody = styled(Body)`
  a {
    ${LinkStyle};
    color: ${({ theme }) => theme.color.bodyText};
  }
`

export const StyledFooter = styled.div`
  background-color: ${({ theme, isEmbeddedPage }) =>
    isEmbeddedPage ? 'none' : theme.color.grey05};
  width: 100%;
  display: flex;
  justify-content: center;
  align-self: flex-end;
  align-items: center;
  font-size: ${({ theme, isEmbeddedPage }) =>
    isEmbeddedPage ? theme.space.s12 : theme.space.s14};
  height: ${({ theme, isEmbeddedPage }) =>
    isEmbeddedPage ? 'auto' : theme.space.s160};
  column-gap: ${({ theme }) => theme.space.s12};
  padding: ${({ theme, isEmbeddedPage }) =>
    isEmbeddedPage ? theme.space.s0 : theme.space.s40};
  flex-direction: ${({ hasMoreLinks }) => (hasMoreLinks ? 'column' : 'row')};
  row-gap: ${({ theme }) => theme.space.s20};

  ${({ theme, hasMoreLinks }) =>
    hasMoreLinks ? theme.mediaQuery.screenM : theme.mediaQuery.screenS} {
    flex-direction: column;
  }

  ${({ theme, hasMoreLinks }) =>
    hasMoreLinks ? theme.mediaQuery.screenS : theme.mediaQuery.tablet} {
    align-items: flex-start;
    height: ${({ theme, hasMoreLinks }) =>
      hasMoreLinks ? theme.space.s320 : theme.space.s240};
  }
`

const FooterLinksContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  column-gap: ${({ theme }) => theme.space.s12};

  ${({ theme, hasMoreLinks }) =>
    hasMoreLinks ? theme.mediaQuery.screenS : theme.mediaQuery.tablet} {
    flex-direction: column;
    align-items: flex-start;
    row-gap: ${({ theme }) => theme.space.s16};
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
  text-align: center;
`

const StyledCopyRightText = styled(Body)`
  font-size: ${({ theme, isEmbeddedPage }) =>
    isEmbeddedPage ? theme.space.s12 : theme.space.s14};
`

const StyledDivider = styled.span`
  margin-bottom: 0px;
  line-height: ${({ theme }) => theme.space.s32};
  display: flex;
  align-items: center;
  width: 0;
  color: transparent;
  border-right: 1px solid ${({ theme }) => theme.color.grey30};

  &:first-child {
    display: ${({ hasMoreLinks }) => hasMoreLinks && 'none'};

    ${({ theme }) => theme.mediaQuery.screenS} {
      display: none;
    }
  }

  ${({ theme, hasMoreLinks }) =>
    hasMoreLinks ? theme.mediaQuery.screenS : theme.mediaQuery.tablet} {
    display: none;
  }
`

const getCustomSupportLink = (customSupport, i18n) => {
  if (customSupport.type === 'email') {
    return `<a href="mailto:${customSupport.value}">${customSupport.value}</a>`
  }

  if (customSupport.type === 'link') {
    return `<a href="${customSupport.value}" target="_blank" rel="noopener noreferrer">
          ${customSupport.companyName}
        </a>`
  }

  return `<a href="mailto:${i18n.supportLink}">${i18n.supportLink}</a>`
}

const customSupportContent = (customSupport, i18n) => {
  if (customSupport) {
    const supportLink = getCustomSupportLink(customSupport, i18n)
    const customSupportText = i18n.footer.customSupport(supportLink)

    return (
      <CustomSupportBody
        data-testid="footer-support-info"
        dangerouslySetInnerHTML={{ __html: customSupportText }}
      />
    )
  }

  return (
    <StyledLink href={`mailto:${i18n.supportLink}`}>
      {i18n.supportLink}
    </StyledLink>
  )
}
export function Footer({
  i18n,
  termsLink,
  licenseLink,
  accessibilityReportLink,
  privacyPolicyLink,
  customSupport,
  isEmbeddedPage,
}) {
  return (
    <StyledFooter isEmbeddedPage={isEmbeddedPage} hasMoreLinks={!!licenseLink}>
      <StyledCopyRightText
        color="bodyText"
        mb="s0"
        isEmbeddedPage={isEmbeddedPage}
      >
        Copyright &copy; 2025 Recright
      </StyledCopyRightText>
      <FooterLinksContainer hasMoreLinks={!!licenseLink}>
        <StyledDivider hasMoreLinks={!!licenseLink}>|</StyledDivider>
        <StyledLink target="_blank" rel="noopener noreferrer" href={termsLink}>
          {i18n.footer.terms}
        </StyledLink>
        <StyledDivider hasMoreLinks={!!licenseLink}>|</StyledDivider>
        <StyledLink
          target="_blank"
          rel="noopener noreferrer"
          href={accessibilityReportLink}
        >
          {i18n.footer.accessibilityReport}
        </StyledLink>
        <StyledDivider hasMoreLinks={!!licenseLink}>|</StyledDivider>
        {licenseLink && (
          <>
            <StyledLink
              target="_blank"
              rel="noopener noreferrer"
              href={licenseLink}
            >
              {i18n.footer.license}
            </StyledLink>
            <StyledDivider hasMoreLinks={!!licenseLink}>|</StyledDivider>
          </>
        )}
        {privacyPolicyLink && (
          <>
            <StyledLink
              target="_blank"
              rel="noopener noreferrer"
              href={privacyPolicyLink}
            >
              {i18n.footer.privacyPolicy}
            </StyledLink>
            <StyledDivider hasMoreLinks={!!licenseLink}>|</StyledDivider>
          </>
        )}
        {customSupportContent(customSupport, i18n)}
      </FooterLinksContainer>
    </StyledFooter>
  )
}

Footer.propTypes = propTypes
Footer.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps())(Footer)
